import IntlMessages from 'helpers/IntlMessages';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

type Offer = {
  link: string;
  kind: 'community' | 'commons' | 'ecoregion' | 'activity';
  accessKind: 'public' | 'members' | 'team';
  role: 'facilitator' | 'teacher' | 'mentor' | null;
  name: string;
  chatName: string;
};

const validId = (value: string | null): value is string =>
  !!value && /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) && Number(value) > 0;

const ChatInviteCreator = (): React.ReactElement => {
  const intl = useIntl();
  const location = useLocation();
  const linkInput = useRef<HTMLInputElement>(null);
  const requestSequence = useRef(0);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revoked, setRevoked] = useState(false);

  const createLink = useCallback(async () => {
    const requestId = ++requestSequence.current;
    const params = new URLSearchParams(location.search);
    const categoryId = params.get('categoryId');
    const channelId = params.get('channelId');
    if (!validId(categoryId) || !validId(channelId)) {
      setOffer(null);
      setError(true);
      setBusy(false);
      setRevoked(false);
      return;
    }
    setOffer(null);
    setBusy(true);
    setError(false);
    setCopied(false);
    setRevoked(false);
    try {
      const response = await fetch('/api/discourse/chat-invitations', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryId: Number(categoryId), channelId: Number(channelId) }),
      });
      if (!response.ok) throw new Error('Could not create invitation');
      const newOffer = await response.json();
      if (requestId === requestSequence.current) setOffer(newOffer);
    } catch {
      if (requestId === requestSequence.current) setError(true);
    } finally {
      if (requestId === requestSequence.current) setBusy(false);
    }
  }, [location.search]);

  useEffect(() => { void createLink(); }, [createLink]);

  const copyLink = async () => {
    if (!offer) return;
    try {
      await navigator.clipboard.writeText(offer.link);
      setCopied(true);
    } catch {
      linkInput.current?.select();
    }
  };

  const shareLink = () => {
    if (!offer) return;
    if (navigator.share) {
      void navigator.share({
        title: intl.formatMessage({ id: 'chat.invite-share-title' }, { chatName: offer.chatName }),
        url: offer.link,
      }).catch(error => { if (error?.name !== 'AbortError') void copyLink(); });
    } else {
      void copyLink();
    }
  };

  const revokeLink = async () => {
    if (!offer) return;
    const requestId = requestSequence.current;
    const revokedOffer = offer;
    try {
      const token = new URL(offer.link).pathname.split('/').pop();
      const response = await fetch(`/api/discourse/chat-invitations/${token}/revoke`, {
        method: 'POST', credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Could not revoke invitation');
      if (requestId === requestSequence.current && revokedOffer === offer) setRevoked(true);
    } catch {
      if (requestId === requestSequence.current && revokedOffer === offer) setError(true);
    }
  };

  const warningId = offer?.accessKind === 'team'
    ? offer.role === 'teacher' ? 'chat.invite-grants-teacher'
      : offer.role === 'facilitator' ? 'chat.invite-grants-facilitator' : 'chat.invite-grants-mentor'
    : offer?.accessKind === 'members' ? 'chat.invite-grants-member' : 'chat.invite-grants-public';

  return <main className="container py-4" style={{ maxWidth: '44rem' }}>
    <Card className="p-4 text-center">
      <img src="/assets/img/stewards.png" alt="" aria-hidden="true"
        style={{ width: '100%', maxWidth: '12rem', height: 'auto', margin: '0 auto 1rem' }} />
      <h1><IntlMessages id="chat.invite-creator-title" /></h1>
      {busy && <div className="mb-3"><Spinner animation="border" role="status" className="mr-2" />
        <IntlMessages id="chat.invite-creating" /></div>}
      {error && <Alert variant="danger"><IntlMessages id="chat.invite-error" /></Alert>}
      {offer && !busy && !revoked && <>
        <p><IntlMessages id="chat.invite-chat-name" values={{ chatName: offer.chatName, name: offer.name }} /></p>
        <Alert variant={offer.accessKind === 'team' ? 'warning' : 'info'}>
          <IntlMessages id={warningId} values={{ name: offer.name }} />
        </Alert>
        <input ref={linkInput} className="form-control mb-3" readOnly value={offer.link}
          aria-label={intl.formatMessage({ id: 'chat.invite-link-label' })} onFocus={event => event.target.select()} />
        <div className="d-flex justify-content-center flex-wrap" style={{ gap: '0.5rem' }}>
          <Button onClick={shareLink}><IntlMessages id="chat.invite-share" /></Button>
          <Button variant="outline-primary" onClick={() => void copyLink()}>
            <IntlMessages id={copied ? 'chat.invite-copied' : 'chat.invite-copy'} />
          </Button>
          <Button variant="outline-danger" onClick={() => void revokeLink()}>
            <IntlMessages id="chat.invite-revoke" />
          </Button>
        </div>
      </>}
      {revoked && <>
        <Alert variant="success"><IntlMessages id="chat.invite-revoked" /></Alert>
        <Button onClick={() => void createLink()}><IntlMessages id="chat.invite-create-again" /></Button>
      </>}
      {error && !busy && <Button variant="outline-primary" onClick={() => void createLink()}>
        <IntlMessages id="general.retry" />
      </Button>}
    </Card>
  </main>;
};

export default ChatInviteCreator;
